/**
 * <mejoris-purchase> — Módulo de Compra oficial del Sistema Mejoris.
 * Selector propio (bundles en tarjetas O variantes en pills) + CTA que
 * activa el botón OFICIAL de Releasit (no reemplaza su lógica, no simula
 * el cobro). El componente se adapta solo según `data-mode`:
 *
 *   data-mode="bundle"  → tarjetas de bundle (radios), ver ejemplo A.
 *   data-mode="variant" → opciones reales del producto (color/talla/…),
 *                          ver ejemplo B. Útil para productos SIN bundles.
 *   data-mode="bundle-variant" → ambos a la vez (opt-in vía show_variant_picker
 *                          en mejoris-purchase.liquid). Para productos con
 *                          ejes reales de proveedor (ropa) que ADEMÁS
 *                          necesitan tiers de cantidad — el precio lo decide
 *                          siempre el tier, no la variante (ver Carga Fácil™).
 *
 * Ambos modos comparten: el form real de /cart/add, el CTA propio, y el
 * mecanismo de disparo del botón oficial de Releasit.
 *
 * ---- Ejemplo A (bundle) ----
 *   <mejoris-purchase class="mpm" data-mode="bundle">
 *     <div class="mpm__card">
 *       <div class="mpm__tiers">
 *         <label class="mpm__tier">
 *           <input type="radio" class="mpm__tier-input" name="mpm-tier-x"
 *                  data-qty="2" data-price-display="$99.900" checked>
 *           ...
 *         </label>
 *       </div>
 *       <div class="mpm__buybox">
 *         <form action="/cart/add">...inputs id/quantity...</form>
 *         <button type="button" class="mpm__cta" data-mpm-cta>
 *           <span data-mpm-cta-price></span>
 *         </button>
 *       </div>
 *     </div>
 *   </mejoris-purchase>
 *
 * ---- Ejemplo B (variant) ----
 *   <mejoris-purchase class="mpm" data-mode="variant">
 *     <script type="application/json" data-mpm-variants>
 *       [{"id":123,"available":true,"option1":"NEGRO","option2":"39","option3":null,"price_display":"$249.900"}, ...]
 *     </script>
 *     <div class="mpm__card">
 *       <div class="mpm__option-group" data-option-index="0">
 *         <button type="button" class="mpm__option-btn is-selected" data-option-index="0" data-option-value="NEGRO" data-image="...">NEGRO</button>
 *         ...
 *       </div>
 *       <div class="mpm__buybox">
 *         <form action="/cart/add">...inputs id/quantity...</form>
 *         <button type="button" class="mpm__cta" data-mpm-cta>
 *           <span data-mpm-cta-price></span>
 *         </button>
 *       </div>
 *     </div>
 *   </mejoris-purchase>
 *
 * Modo bundle: elegir un tier actualiza `input[name="quantity"]` del form
 * real (Releasit lee ese valor al abrir su modal — sus propias Quantity
 * Offers muestran el descuento correcto para esa cantidad, sin necesitar
 * variantes extra en Shopify).
 *
 * Modo variant: elegir una opción recalcula qué variante real coincide
 * (comparando option1/option2/option3, igual que el patrón nativo de
 * Shopify) y actualiza `input[name="id"]` del form real. Si la variante
 * no existe o no tiene stock, el CTA queda deshabilitado con un aviso —
 * nunca se dispara Releasit con una variante inválida o agotada.
 *
 * En ambos modos, al tocar el CTA visual: sincroniza el form una última
 * vez y dispara un click real sobre el botón oficial de Releasit (que el
 * propio script de Releasit ya inyectó en el form, oculto visualmente vía
 * CSS pero intacto y clicable).
 */
(function () {
  if (customElements.get('mejoris-purchase')) return;

  var RELEASIT_BUTTON_SELECTOR = '#_rsi-buy-now-button';
  var POLL_INTERVAL_MS = 100;
  var POLL_MAX_ATTEMPTS = 30;
  var GALLERY_EVENT = 'mejoris:gallery-set-image';

  // Orden lógico para tallas alfanuméricas (no numéricas). Cubre los
  // formatos más comunes; cualquier otro conjunto de valores (ej. colores)
  // no coincide con esto y queda sin reordenar — ver sortOptionButtons.
  var ALPHA_SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '2XL', '3XL', '4XL', '5XL'];

  // Reordena botones de una opción del selector de variantes: numérico
  // ascendente si TODOS los valores son números (tallas de calzado, etc.),
  // orden lógico si TODOS son tallas alfanuméricas conocidas (S/M/L/...),
  // o el orden original (el que trae Shopify) para cualquier otro caso —
  // como COLOR, que nunca debe reordenarse por esta lógica.
  // Formatea un valor de precio "crudo" (la misma unidad que expone Liquid
  // para `variant.price`, ya multiplicada x100) como pesos colombianos:
  // "$154.900". Solo se usa para el precio CON descuento, calculado en el
  // navegador — el precio original siempre viene ya formateado por Liquid
  // (`price_display`), así que nunca hace falta formatear moneda en JS
  // salvo para este caso derivado.
  function formatMoney(rawValue) {
    var pesos = Math.round(rawValue / 100);
    return '$' + pesos.toLocaleString('es-CO');
  }

  function sortOptionButtons(buttons) {
    var values = buttons.map(function (b) { return (b.getAttribute('data-option-value') || '').trim(); });

    var allNumeric = values.length > 0 && values.every(function (v) { return /^\d+(\.\d+)?$/.test(v); });
    if (allNumeric) {
      return buttons.slice().sort(function (a, b) {
        return parseFloat(a.getAttribute('data-option-value')) - parseFloat(b.getAttribute('data-option-value'));
      });
    }

    var allAlphaSize = values.length > 0 && values.every(function (v) { return ALPHA_SIZE_ORDER.indexOf(v.toUpperCase()) !== -1; });
    if (allAlphaSize) {
      return buttons.slice().sort(function (a, b) {
        var av = a.getAttribute('data-option-value').trim().toUpperCase();
        var bv = b.getAttribute('data-option-value').trim().toUpperCase();
        return ALPHA_SIZE_ORDER.indexOf(av) - ALPHA_SIZE_ORDER.indexOf(bv);
      });
    }

    return buttons;
  }

  class MejorisPurchase extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[action="/cart/add"]');
      this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.qtyInput = this.form ? this.form.querySelector('input[name="quantity"]') : null;
      this.ctaBtn = this.querySelector('[data-mpm-cta]');
      this.ctaPrice = this.querySelector('[data-mpm-cta-price]');
      this.ctaPriceBefore = this.querySelector('[data-mpm-cta-price-before]');
      this.ctaText = this.querySelector('.mpm__cta-text');
      this.unavailableNote = this.querySelector('[data-mpm-unavailable]');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-mpm-thumb]'));
      this.mainImg = this.querySelector('[data-mpm-main-img]');
      this._ctaDefaultText = this.ctaText ? this.ctaText.textContent : '';
      // Descuento real que aplica Releasit al abrir el modal, cuando el
      // precio de Shopify no es el precio final (ver mejoris-purchase.liquid,
      // parámetros sale_percent / sale_discount_amount). Monto fijo tiene
      // prioridad sobre porcentaje si se pasaron los dos.
      this.salePercent = parseFloat(this.dataset.salePercent) || 0;
      this.saleDiscountAmount = parseFloat(this.dataset.saleDiscountAmount) || 0;
      // Precio base ya resuelto por Liquid para modo simple (variante única,
      // con sale_percent/sale_discount_amount ya aplicado si corresponde).
      // En modo bundle/variant el precio base se recalcula dinámicamente
      // (ver _computeBaseFinalRaw) y este valor no se usa.
      this.simplePriceRaw = parseFloat(this.dataset.simplePriceRaw) || 0;

      // Selector de método de pago (tarjeta con descuento vs contra entrega).
      // Solo existe si la landing pasó card_discount_percent + card_discount_code.
      this.cardDiscountPercent = parseFloat(this.dataset.cardDiscountPercent) || 0;
      this.cardDiscountCode = this.dataset.cardDiscountCode || '';
      this.paymentToggleEnabled = this.cardDiscountPercent > 0 && !!this.cardDiscountCode;
      this.paymentMethod = 'card'; // default: la opción recomendada, igual que el radio marcado por Liquid

      this._bindGallery();
      this._bindCta();
      this._bindPaymentToggle();
      this._bindFloatingButtonFallback();

      if (this.dataset.mode === 'variant') {
        this._initVariantMode();
      } else if (this.dataset.mode === 'bundle-variant') {
        // Modo combinado (opt-in vía show_variant_picker en mejoris-purchase.liquid):
        // pills de variante real (color/talla) + tarjetas de cantidad al mismo
        // tiempo. Ambos inits son independientes (operan sobre subconjuntos de
        // DOM distintos: .mpm__tier-input vs .mpm__option-btn) — ver los guards
        // por modo en _applyVariantState/_bindCta/_computeBaseFinalRaw/
        // _syncReleasitQuantityOffer para cómo se combinan sin pisarse.
        this._initBundleMode();
        this._initVariantMode();
      } else {
        this._initBundleMode();
      }

      this._updatePaymentPrices();
    }

    // ============================================================
    // Modo bundle (tarjetas de cantidad)
    // ============================================================
    _initBundleMode() {
      this.tierInputs = Array.prototype.slice.call(this.querySelectorAll('.mpm__tier-input'));
      var self = this;
      this.tierInputs.forEach(function (input) {
        input.addEventListener('change', function () { self._applyTier(input); });
      });
      var checked = this.tierInputs.filter(function (i) { return i.checked; })[0];
      if (checked) this._applyTier(checked);
    }

    _applyTier(input) {
      this.tierInputs.forEach(function (i) {
        i.closest('.mpm__tier').classList.toggle('is-selected', i === input);
      });
      var qty = input.getAttribute('data-qty');
      if (this.qtyInput && qty) {
        this.qtyInput.value = qty;
        this.qtyInput.setAttribute('value', qty);
      }
      this._currentTierRaw = parseFloat(input.getAttribute('data-price-raw')) || null;
      var priceDisplay = input.getAttribute('data-price-display');
      if (this.ctaPrice && priceDisplay && !this.paymentToggleEnabled) this.ctaPrice.textContent = priceDisplay;
      this._updatePaymentPrices();
    }

    // ============================================================
    // Modo variant (opciones reales del producto: color, talla, ...)
    // ============================================================
    _initVariantMode() {
      var variantsScript = this.querySelector('[data-mpm-variants]');
      this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];

      this._sortOptionGroups();

      this.optionButtons = Array.prototype.slice.call(this.querySelectorAll('.mpm__option-btn'));

      this.optionGroupCount = 0;
      this.optionButtons.forEach(function (btn) {
        var idx = parseInt(btn.getAttribute('data-option-index'), 10);
        if (idx + 1 > this.optionGroupCount) this.optionGroupCount = idx + 1;
      }, this);

      // Estado inicial: lo que el servidor ya marcó como .is-selected por
      // grupo (Liquid preselecciona la primera variante disponible), con
      // fallback al primer botón de cada grupo si algo no vino marcado.
      this.selected = [];
      for (var i = 0; i < this.optionGroupCount; i++) {
        var groupBtns = this.optionButtons.filter(function (b) {
          return parseInt(b.getAttribute('data-option-index'), 10) === i;
        });
        var selectedBtn = groupBtns.filter(function (b) { return b.classList.contains('is-selected'); })[0] || groupBtns[0];
        this.selected[i] = selectedBtn ? selectedBtn.getAttribute('data-option-value') : null;
      }

      var self = this;
      this.optionButtons.forEach(function (btn) {
        btn.addEventListener('click', function () { self._selectOption(btn); });
      });

      this._applyVariantState();
    }

    // Reordena cada grupo de opciones en el DOM (ej. TALLA: 37 38 39... en
    // vez del orden que trae Shopify). No afecta la opción de swatches
    // (COLOR) ni ninguna otra opción no numérica/no alfanumérica de talla —
    // ver sortOptionButtons.
    _sortOptionGroups() {
      var groups = Array.prototype.slice.call(this.querySelectorAll('.mpm__option-group'));
      groups.forEach(function (group) {
        var container = group.querySelector('.mpm__option-values');
        if (!container) return;
        var buttons = Array.prototype.slice.call(container.querySelectorAll('.mpm__option-btn'));
        var sorted = sortOptionButtons(buttons);
        sorted.forEach(function (btn) { container.appendChild(btn); });
      });
    }

    _selectOption(btn) {
      var index = parseInt(btn.getAttribute('data-option-index'), 10);
      var value = btn.getAttribute('data-option-value');
      this.selected[index] = value;

      this.optionButtons
        .filter(function (b) { return parseInt(b.getAttribute('data-option-index'), 10) === index; })
        .forEach(function (b) { b.classList.toggle('is-selected', b === btn); });

      var image = btn.getAttribute('data-image');
      if (image) {
        if (this.mainImg) this.mainImg.src = image;
        document.dispatchEvent(new CustomEvent(GALLERY_EVENT, { detail: { url: image } }));
      }

      this._applyVariantState();
    }

    _findMatchingVariant() {
      var selected = this.selected;
      return this.variants.filter(function (v) {
        var keys = ['option1', 'option2', 'option3'];
        for (var i = 0; i < selected.length; i++) {
          if (v[keys[i]] !== selected[i]) return false;
        }
        return true;
      })[0] || null;
    }

    _applyVariantState() {
      var variant = this._findMatchingVariant();
      this._currentVariant = variant;

      if (this.idInput && variant) {
        this.idInput.value = variant.id;
        this.idInput.setAttribute('value', variant.id);
      }

      var disabled = !variant || !variant.available;
      if (this.ctaBtn) this.ctaBtn.disabled = disabled;

      if (this.unavailableNote) {
        this.unavailableNote.hidden = !!variant && variant.available;
        this.unavailableNote.textContent = !variant
          ? 'Esta combinación no está disponible.'
          : 'Esta opción está agotada por ahora.';
      }

      if (this.paymentToggleEnabled) {
        this._updatePaymentPrices();
      } else if (this.dataset.mode === 'variant') {
        // En 'bundle-variant' el precio lo gobierna el tier seleccionado
        // (_applyTier), no la variante — todas las variantes comparten el
        // mismo precio base, así que pintar aquí el precio de la variante
        // pisaría el precio del tier 2/3 cada vez que se toca color/talla.
        this._applyPriceDisplay(variant);
      }
      if (this.ctaText) this.ctaText.textContent = disabled ? 'No disponible' : this._ctaDefaultText;
    }

    // Calcula y pinta el precio del CTA para la variante dada. Si hay un
    // descuento configurado (sale_percent o sale_discount_amount), muestra
    // el precio original tachado + el precio final que realmente cobra
    // Releasit; si no, muestra el precio real de la variante tal cual.
    _applyPriceDisplay(variant) {
      if (!this.ctaPrice) return;
      if (!variant) {
        this.ctaPrice.textContent = '';
        if (this.ctaPriceBefore) this.ctaPriceBefore.textContent = '';
        return;
      }

      var saleRaw = null;
      if (this.saleDiscountAmount > 0) {
        saleRaw = variant.price - (this.saleDiscountAmount * 100);
      } else if (this.salePercent > 0) {
        saleRaw = variant.price * (100 - this.salePercent) / 100;
      }

      if (saleRaw !== null && saleRaw > 0) {
        this.ctaPrice.textContent = formatMoney(saleRaw);
        if (this.ctaPriceBefore) this.ctaPriceBefore.textContent = variant.price_display;
      } else {
        this.ctaPrice.textContent = variant.price_display;
        if (this.ctaPriceBefore) this.ctaPriceBefore.textContent = '';
      }
    }

    // ============================================================
    // Compartido: galería propia (si el módulo la incluye) y CTA
    // ============================================================
    _bindGallery() {
      var self = this;
      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var full = thumb.getAttribute('data-full');
          if (full && self.mainImg) self.mainImg.src = full;
          self.thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
        });
      });
    }

    _bindCta() {
      if (!this.ctaBtn) return;
      var self = this;
      this.ctaBtn.addEventListener('click', function () {
        if (self.ctaBtn.disabled) return;
        // Re-sincroniza justo antes de disparar: defensa contra cualquier
        // desajuste entre la selección visible y el valor real del form
        // (ej. bfcache/autofill del navegador restaurando el form sin
        // disparar los eventos de selección).
        if (self.dataset.mode === 'variant' || self.dataset.mode === 'bundle-variant') {
          self._applyVariantState();
          if (self.ctaBtn.disabled) return;
        }
        if (self.dataset.mode === 'bundle' || self.dataset.mode === 'bundle-variant') {
          var checked = self.tierInputs && self.tierInputs.filter(function (i) { return i.checked; })[0];
          if (checked) self._applyTier(checked);
        }
        // LIMITACIÓN REAL CONFIRMADA (evidencia en el commit que agregó esto):
        // el resumen "Subtotal/Total" del modal de Releasit lee el carrito
        // real de Shopify (mismo mecanismo que /cart.js), y un discount CODE
        // (a diferencia de un descuento automático) nunca modifica el
        // carrito — solo se activa al crear un checkout real. Confirmado en
        // vivo: con la cookie de descuento ya puesta y "Tarjeta" ya
        // seleccionada, el modal seguía mostrando Subtotal/Total sin
        // descontar. No hay forma de hacer que el formulario COMPARTIDO de
        // Releasit muestre un precio distinto al del carrito real sin una
        // Shopify Function de descuento (infraestructura de app nueva, fuera
        // de este theme). En vez de mostrarle al cliente un precio que no
        // corresponde en un paso intermedio, el flujo de tarjeta se SALTA
        // ese formulario compartido por completo: agrega el producto real
        // al carrito, aplica el descuento, y navega directo al checkout
        // real de Shopify — el mismo checkout que Releasit usaría igual si
        // el cliente completara su formulario y tocara "otros medios", solo
        // que sin pasar antes por una pantalla con el precio equivocado.
        if (self.paymentToggleEnabled && self.paymentMethod === 'card') {
          self._goToCardCheckout();
          return;
        }
        self._triggerReleasit();
      });
    }

    // ============================================================
    // Selector de método de pago (tarjeta con descuento vs contra entrega)
    // ============================================================
    _bindPaymentToggle() {
      if (!this.paymentToggleEnabled) return;
      var inputs = Array.prototype.slice.call(this.querySelectorAll('[data-mpm-payment-input]'));
      if (!inputs.length) return;
      this.paymentInputs = inputs;
      this.paymentHint = this.querySelector('[data-mpm-payment-hint]');
      var self = this;
      inputs.forEach(function (input) {
        input.addEventListener('change', function () {
          if (!input.checked) return;
          self.paymentMethod = input.value;
          inputs.forEach(function (i) {
            i.closest('.mpm__payment-option').classList.toggle('is-selected', i === input);
          });
          self._updatePaymentPrices();
        });
      });
    }

    // Precio "normal" (contra entrega) vigente ahora mismo, según el modo:
    // bundle = tier seleccionado, variant = variante que matchea la
    // selección actual (con su sale_percent/sale_discount_amount si aplica),
    // simple = el único precio ya resuelto por Liquid al cargar la página.
    _computeBaseFinalRaw() {
      if (this.dataset.mode === 'variant') {
        if (!this._currentVariant) return null;
        var v = this._currentVariant;
        if (this.saleDiscountAmount > 0) return v.price - (this.saleDiscountAmount * 100);
        if (this.salePercent > 0) return v.price * (100 - this.salePercent) / 100;
        return v.price;
      }
      if (this.dataset.mode === 'bundle' || this.dataset.mode === 'bundle-variant') {
        return this._currentTierRaw || null;
      }
      return this.simplePriceRaw || null;
    }

    _updatePaymentPrices() {
      if (!this.paymentToggleEnabled) return;
      var base = this._computeBaseFinalRaw();
      if (base === null || isNaN(base)) return;

      var cardRaw = Math.round(base * (100 - this.cardDiscountPercent) / 100);
      var savingsRaw = base - cardRaw;

      var cardPriceEl = this.querySelector('[data-mpm-payment-price="card"]');
      var codPriceEl = this.querySelector('[data-mpm-payment-price="cod"]');
      var savingsEl = this.querySelector('[data-mpm-payment-savings]');
      if (cardPriceEl) cardPriceEl.textContent = formatMoney(cardRaw);
      if (codPriceEl) codPriceEl.textContent = formatMoney(base);
      if (savingsEl) savingsEl.textContent = formatMoney(savingsRaw);

      // El CTA principal siempre refleja el método de pago elegido: precio
      // con descuento si eligió tarjeta, precio normal si eligió contra
      // entrega — transparencia total antes de siquiera abrir el modal.
      if (this.ctaPrice) {
        var selectedRaw = this.paymentMethod === 'card' ? cardRaw : base;
        this.ctaPrice.textContent = formatMoney(selectedRaw);
        if (this.ctaPriceBefore) {
          this.ctaPriceBefore.textContent = this.paymentMethod === 'card' ? formatMoney(base) : '';
        }
      }

      if (this.paymentHint) {
        this.paymentHint.textContent = this.paymentMethod === 'card'
          ? 'Tu ' + this.cardDiscountPercent + '% de descuento ya está aplicado — vas directo al pago seguro, sin pasos de más.'
          : 'Tu pedido se paga al recibirlo — sin costos adicionales.';
      }
    }

    // Releasit inyecta, ADEMÁS del botón oficial dentro del form (el que
    // dispara _triggerReleasit), un botón flotante independiente en el
    // <body> cuando `buyNowButton.floatingEnabled` está activo en su
    // configuración (aparece al hacer scroll más allá del módulo de
    // compra — mismo comportamiento confirmado en Botas y Sentinel Lock).
    // Ese flotante abre EL MISMO modal, pero con su propio listener de
    // click, fuera de nuestro control - si el cliente eligio "Tarjeta" en
    // nuestro selector y luego compra usando el flotante en vez de nuestro
    // CTA visual, terminaria en el modal de COD sin el descuento de
    // tarjeta aplicado. Se escucha en fase de captura sobre `document`
    // (el boton se inserta de forma asincrona por el script de Releasit,
    // no existe al cargar la pagina) para cubrir ese camino tambien.
    _bindFloatingButtonFallback() {
      if (!this.paymentToggleEnabled) return;
      var self = this;
      document.addEventListener('click', function (e) {
        var target = e.target && e.target.closest
          ? e.target.closest('#_rsi-buy-now-button-floating, .rsi_buy_now_button, #rsi_buy_now_button')
          : null;
        if (!target) return;
        if (self.paymentMethod !== 'card') return;
        // Mismo criterio que el CTA visual: si el cliente eligio
        // "Tarjeta", el flotante de Releasit tampoco debe abrir su modal
        // de COD - debe ir directo a checkout con descuento (ver
        // _goToCardCheckout). Se corta el evento en fase de captura
        // antes de que el propio listener de Releasit lo reciba.
        e.preventDefault();
        e.stopPropagation();
        self._goToCardCheckout();
      }, true);
    }

    // El modal COMPARTIDO de Releasit lee el carrito real de Shopify, y un
    // discount CODE (a diferencia de uno automatico) nunca lo modifica -
    // solo se aplica al crear un checkout real (confirmado en vivo). Por
    // eso el camino de tarjeta no abre ese modal: navega directo a un
    // "cart permalink" de Shopify (/cart/{variant}:{qty}?discount={code}),
    // que agrega la variante, aplica el descuento Y redirige al checkout
    // real EN UNA SOLA peticion server-side - Shopify nunca renderiza
    // /cart en el camino (confirmado en vivo: la navegacion aterriza
    // directo en /checkouts/...). Sin selector de cantidad en la landing
    // (decision de producto: una sola decision de compra, sin pasos de
    // mas) - qty siempre es el valor por defecto del form (1, o el del
    // tier en modo bundle).
    _goToCardCheckout() {
      if (!this.idInput || !this.qtyInput || !this.cardDiscountCode) return;
      var variantId = this.idInput.value;
      var qty = parseInt(this.qtyInput.value, 10) || 1;
      if (this.ctaBtn) this.ctaBtn.disabled = true;
      if (this.ctaText) this.ctaText.textContent = 'Redirigiendo al pago...';

      var url = '/cart/' + encodeURIComponent(variantId) + ':' + qty
        + '?discount=' + encodeURIComponent(this.cardDiscountCode);
      window.location.href = url;
    }

    _findReleasitButton() {
      if (!this.form) return null;
      var btn = this.form.querySelector(RELEASIT_BUTTON_SELECTOR);
      if (btn) return btn;
      return this.querySelector(RELEASIT_BUTTON_SELECTOR);
    }

    _triggerReleasit() {
      var self = this;
      var btn = this._findReleasitButton();
      if (btn) {
        btn.click();
        self._syncReleasitQuantityOffer();
        return;
      }
      var attempts = 0;
      var poll = setInterval(function () {
        attempts++;
        var b = self._findReleasitButton();
        if (b) {
          clearInterval(poll);
          b.click();
          self._syncReleasitQuantityOffer();
          return;
        }
        if (attempts >= POLL_MAX_ATTEMPTS) {
          clearInterval(poll);
          if (window._rsiV2 && typeof window._rsiV2.setState === 'function') {
            try { window._rsiV2.setState({ isOpen: true }); } catch (e) {}
          }
        }
      }, POLL_INTERVAL_MS);
    }

    // GOTCHA confirmado con Playwright (2026-07-09): Releasit reconstruye
    // el modal completo (lee input[name=quantity], recalcula la Quantity
    // Offer) solo la PRIMERA vez que se abre en la sesión de la página.
    // Reaperturas posteriores reutilizan su propio estado interno de
    // "oferta seleccionada" (window._rsi.form.quantityOffers.selectedOfferIn)
    // — que NO se resincroniza con solo actualizar input[name=quantity]
    // antes de hacer clic otra vez. Bug reproducido: seleccionar 3 Kits
    // (modal correcto) → cerrar → seleccionar 1 Kit → el modal seguía
    // cobrando el total de 3 Kits.
    //
    // Fix: llamar al mismo método interno que Releasit usa cuando el
    // cliente hace clic en una oferta DENTRO de su propio modal
    // (quantityOffers.onSelectOffer(pos)) con el `pos` (índice 0-based)
    // de nuestra tarjeta seleccionada. El orden de las Quantity Offers en
    // el panel de Releasit siempre coincide con el orden en que se
    // cargaron (mismo patrón en todos los productos ya configurados —
    // ver metafield _rsi_cod_form_sf.quantity_offers_json), así que el
    // índice de la tarjeta seleccionada en nuestro propio DOM es siempre
    // el `pos` correcto — no depende de la cantidad (qty) de cada tier.
    //
    // Verificado con Playwright las 6 combinaciones pedidas (1→2, 2→3,
    // 3→1, 3→2, 2→1, 1→3) más repeticiones: 8/8 PASS.
    //
    // Defensivo por diseño: si Releasit cambia esta API interna en una
    // futura actualización de su app, este método simplemente no hace
    // nada (try/catch + chequeo de existencia) — nunca rompe el flujo
    // base que ya funciona (abrir el modal, cobrar según input[quantity]
    // en la primera apertura).
    _syncReleasitQuantityOffer() {
      var mode = this.dataset.mode;
      if ((mode !== 'bundle' && mode !== 'bundle-variant') || !this.tierInputs) return;
      var pos = this.tierInputs.findIndex(function (i) { return i.checked; });
      if (pos < 0) return;
      var attempts = 0;
      var poll = setInterval(function () {
        attempts++;
        try {
          var container = document.querySelector('[data-offer-pos="' + pos + '"]');
          if (
            container &&
            window._rsi &&
            window._rsi.form &&
            window._rsi.form.quantityOffers &&
            typeof window._rsi.form.quantityOffers.onSelectOffer === 'function'
          ) {
            window._rsi.form.quantityOffers.onSelectOffer(pos);
            clearInterval(poll);
            return;
          }
        } catch (e) {
          clearInterval(poll);
          return;
        }
        if (attempts >= POLL_MAX_ATTEMPTS) clearInterval(poll);
      }, POLL_INTERVAL_MS);
    }
  }

  customElements.define('mejoris-purchase', MejorisPurchase);
})();
